/**
 * @fileoverview Parameter-binding editor inside the schedule editor — one binding row
 *             per declared report parameter (relative token vs fixed value).
 * @module ui-admin/components/ParameterEditor/ParameterBindingSection
 *
 * Responsibilities: - Render a binding row for every declared parameter on the
 *                     schedule's report: mode select (Relative for date/dateRange,
 *                     Fixed otherwise), a token picker for relative bindings, and a
 *                     type-appropriate value input for fixed bindings.
 * Dependencies:     - React, @firesquirrel/core-domain (types),
 *                     ./relativeDateTokens, ./parameterValidators.
 * Architecture:     - Presentation-only, controlled (bindings + onChange). The host
 *                     persists via createReportScheduleWithBindings /
 *                     updateReportScheduleBindings, which re-validate server-side
 *                     equivalence.
 * Notes:            - Required parameters that have no binding are flagged inline —
 *                     the service layer will reject the save regardless.
 */

import React from 'react';
import type { ParameterBinding, ReportParameter, RelativeDateToken } from '@firesquirrel/core-domain';
import { RELATIVE_DATE_TOKEN_OPTIONS } from './relativeDateTokens';

const RELATIVE_CAPABLE: ReadonlySet<ReportParameter['type']> = new Set(['date', 'dateRange']);

/**
 * @contract ParameterBindingSectionProps
 * Purpose: Props for the schedule parameter-binding editor.
 * Fields:
 * - parameters: declared parameters on the schedule's report definition.
 * - bindings: current bindings (controlled).
 * - onChange: emit the patched bindings array.
 * - lookupOptions: option lists keyed by parameterId for fixed lookup bindings.
 * - errors: field-level messages keyed by parameterId (from the host's
 *   save-time validation).
 */
export interface ParameterBindingSectionProps {
  parameters: ReportParameter[];
  bindings: ParameterBinding[];
  onChange: (bindings: ParameterBinding[]) => void;
  lookupOptions?: Record<string, Array<{ value: string; label: string }>>;
  errors?: Record<string, string>;
}

const inputCls =
  'rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800 focus:border-orange-400 focus:outline-none';

/**
 * @component ParameterBindingSection
 * Purpose:     Edit a schedule's parameter bindings (resolved at each scheduled run).
 * Props:       ParameterBindingSectionProps.
 * Behavior:    One row per declared parameter. Mode switch toggles the input
 *              between token picker (relative) and type-driven value input
 *              (fixed). Required-but-unbound rows show an inline warning.
 * Data Source: none (controlled).
 * Constraints: presentation-only; bindings are persisted by the host service.
 */
export function ParameterBindingSection({
  parameters, bindings, onChange, lookupOptions, errors,
}: ParameterBindingSectionProps) {
  const upsert = (parameterId: string, patch: Partial<ParameterBinding>) => {
    const existing = bindings.find((b) => b.parameterId === parameterId);
    const next: ParameterBinding = existing
      ? { ...existing, ...patch }
      : { parameterId, mode: 'fixed' as const, ...patch };
    if (existing) {
      onChange(bindings.map((b) => (b.parameterId === parameterId ? next : b)));
    } else {
      onChange([...bindings, next]);
    }
  };

  const renderValueInput = (param: ReportParameter, binding: ParameterBinding | undefined) => {
    const v = binding?.value;
    if (param.type === 'dateRange') {
      const range = Array.isArray(v) ? (v as [string, string]) : [undefined, undefined];
      return (
        <div className="flex items-center gap-1.5">
          <input type="date" aria-label={`${param.label} start`} className={inputCls}
            value={range[0]?.slice(0, 10) ?? ''}
            onChange={(e) => upsert(param.parameterId, { value: [e.target.value, range[1] ?? e.target.value] })} />
          <span className="text-xs text-gray-400">to</span>
          <input type="date" aria-label={`${param.label} end`} className={inputCls}
            value={range[1]?.slice(0, 10) ?? ''}
            onChange={(e) => upsert(param.parameterId, { value: [range[0] ?? e.target.value, e.target.value] })} />
        </div>
      );
    }
    if (param.type === 'date') {
      return (
        <input type="date" aria-label={param.label} className={inputCls}
          value={typeof v === 'string' ? v.slice(0, 10) : ''}
          onChange={(e) => upsert(param.parameterId, { value: e.target.value })} />
      );
    }
    if (param.type === 'boolean') {
      return (
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input type="checkbox" aria-label={param.label} checked={v === true}
            onChange={(e) => upsert(param.parameterId, { value: e.target.checked })} />
          Yes
        </label>
      );
    }
    if (param.type === 'lookup' || param.type === 'inspectionType' || param.type === 'entity' || param.type === 'account') {
      const opts = lookupOptions?.[param.parameterId] ?? [];
      if (param.options?.multi) {
        const selected = Array.isArray(v) ? (v as string[]) : [];
        return (
          <div className="flex flex-wrap gap-2" role="group" aria-label={param.label}>
            {opts.map((o) => (
              <label key={o.value} className="flex items-center gap-1 text-xs text-gray-700">
                <input type="checkbox" checked={selected.includes(o.value)}
                  onChange={(e) => upsert(param.parameterId, {
                    value: e.target.checked ? [...selected, o.value] : selected.filter((x) => x !== o.value),
                  })} />
                {o.label}
              </label>
            ))}
            {opts.length === 0 && <span className="text-[11px] italic text-gray-400">No options loaded.</span>}
          </div>
        );
      }
      return (
        <select aria-label={param.label} className={inputCls}
          value={typeof v === 'string' ? v : ''}
          onChange={(e) => upsert(param.parameterId, { value: e.target.value || undefined })}>
          <option value="">— select —</option>
          {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    return (
      <input
        type={param.type === 'number' ? 'number' : 'text'}
        aria-label={param.label}
        className={inputCls}
        value={v === undefined || v === null ? '' : String(v)}
        onChange={(e) => upsert(param.parameterId, {
          value: param.type === 'number' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value,
        })} />
    );
  };

  if (parameters.length === 0) return null;

  return (
    <fieldset
      data-testid="parameter-binding-section"
      className="rounded-md border border-violet-200 bg-violet-50/50 p-2.5"
    >
      <legend className="px-1 text-[11px] font-medium text-violet-700">
        Parameter Bindings (resolved at each scheduled run)
      </legend>
      <div className="flex flex-col gap-2">
        {parameters.map((param) => {
          const binding = bindings.find((b) => b.parameterId === param.parameterId);
          const relativeCapable = RELATIVE_CAPABLE.has(param.type);
          const mode = relativeCapable ? (binding?.mode ?? 'fixed') : 'fixed';
          const unboundRequired = param.required && !binding;
          return (
            <div key={param.parameterId} className="flex flex-wrap items-center gap-2">
              <span className="min-w-28 text-xs font-medium text-gray-700">
                {param.label}{param.required && <span className="text-red-500"> *</span>}
              </span>
              {relativeCapable && (
                <select
                  aria-label={`${param.label} binding mode`}
                  value={mode}
                  onChange={(e) => {
                    const m = e.target.value as ParameterBinding['mode'];
                    upsert(param.parameterId, m === 'relative'
                      ? { mode: 'relative', token: (binding?.token ?? 'previousMonth') as RelativeDateToken, value: undefined }
                      : { mode: 'fixed', token: undefined, value: undefined });
                  }}
                  className={inputCls}
                >
                  <option value="fixed">Fixed</option>
                  <option value="relative">Relative</option>
                </select>
              )}
              {mode === 'relative' ? (
                <select
                  aria-label={`${param.label} token`}
                  value={binding?.token ?? 'previousMonth'}
                  onChange={(e) => upsert(param.parameterId, { mode: 'relative', token: e.target.value as RelativeDateToken, value: undefined })}
                  className={inputCls}
                >
                  {RELATIVE_DATE_TOKEN_OPTIONS.map((o) => (
                    <option key={o.token} value={o.token}>{o.label}</option>
                  ))}
                </select>
              ) : (
                renderValueInput(param, binding)
              )}
              {unboundRequired && (
                <span role="alert" className="text-[11px] text-amber-600">required — bind a value</span>
              )}
              {errors?.[param.parameterId] && (
                <span role="alert" className="text-[11px] text-red-600">{errors[param.parameterId]}</span>
              )}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
